// The one sheet the app currently needs: confirm a Stars or
// Premium purchase, optionally as a gift. Composed from the
// generic BottomSheet shell — swap this out (or add a sibling)
// the day a second sheet-driven flow shows up.

import { useEffect, useRef, useState } from 'react';
import { cx } from '@/utils/cx';
import { formatCurrency } from '@/utils/format';
import { CrystalMark, StarIcon } from '@/assets/icons';
import { BottomSheet } from '@/components/BottomSheet';
import type { UseBottomSheetResult } from '@/hooks/useBottomSheet';
import { isStarProduct } from '@/types/product';
import type { Product } from '@/types/product';
import type { RecipientInfo } from '@/types/order';
import styles from './PurchaseSheet.module.scss';

export interface PurchaseSheetProps {
  product: Product | null;
  bottomSheet: UseBottomSheetResult;
  isSubmitting: boolean;
  onConfirm: (recipient: RecipientInfo) => void;
}

export function PurchaseSheet({ product, bottomSheet, isSubmitting, onConfirm }: PurchaseSheetProps) {
  const [isGift, setIsGift] = useState(false);
  const [giftUsername, setGiftUsername] = useState('');
  const [showError, setShowError] = useState(false);
  const giftInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setIsGift(false);
    setGiftUsername('');
    setShowError(false);
  }, [product?.id]);

  if (!product) {
    return <BottomSheet bottomSheet={bottomSheet}>{null}</BottomSheet>;
  }

  const subtitle = isStarProduct(product) ? 'One-time purchase' : product.subtitle ?? 'Recurring subscription';

  const handleConfirm = () => {
    if (isGift && !giftUsername.trim()) {
      setShowError(true);
      giftInputRef.current?.focus();
      return;
    }
    const recipient: RecipientInfo = isGift
      ? { type: 'gift', username: giftUsername.trim().replace(/^@/, '') }
      : { type: 'self' };
    onConfirm(recipient);
  };

  return (
    <BottomSheet bottomSheet={bottomSheet}>
      <div className={styles.item}>
        <div className={styles.itemIcon}>
          {isStarProduct(product) ? <StarIcon size={22} /> : <CrystalMark size={22} />}
        </div>
        <div>
          <div className={styles.itemTitle}>{product.title}</div>
          <div className={styles.itemSub}>{subtitle}</div>
        </div>
      </div>

      <div className={styles.toggle}>
        <button
          type="button"
          className={cx(styles.toggleButton, !isGift && styles.active)}
          onClick={() => setIsGift(false)}
        >
          For myself
        </button>
        <button
          type="button"
          className={cx(styles.toggleButton, isGift && styles.active)}
          onClick={() => setIsGift(true)}
        >
          Gift to a friend
        </button>
      </div>

      <div className={cx(styles.giftInputWrap, isGift && styles.open)}>
        <input
          ref={giftInputRef}
          className={cx(styles.giftInput, showError && styles.invalid)}
          placeholder="@username"
          autoComplete="off"
          value={giftUsername}
          onChange={(event) => {
            setGiftUsername(event.target.value);
            if (showError) setShowError(false);
          }}
        />
      </div>

      <div className={styles.totals}>
        <div className={styles.totalsRow}>
          <span>Subtotal</span>
          <span>{formatCurrency(product.price, product.currency)}</span>
        </div>
        <div className={styles.totalsRow}>
          <span>Service fee</span>
          <span>{formatCurrency(0, product.currency)}</span>
        </div>
        <div className={cx(styles.totalsRow, styles.total)}>
          <span>Total</span>
          <span>{formatCurrency(product.price, product.currency)}</span>
        </div>
      </div>

      <button type="button" className={styles.cta} disabled={isSubmitting} onClick={handleConfirm}>
        {isSubmitting ? 'Processing…' : 'Buy'}
      </button>
    </BottomSheet>
  );
}
